import React, { useEffect, useRef } from "react";
import { useInView, useMotionValue, animate } from "framer-motion";

/**
 * Animated number counter that preserves prefix/suffix from a display string.
 * e.g. "18,400+" -> counts 0..18400 with trailing "+";  "6.4×" -> 0..6.4 with "×".
 */
export default function CountUp({ value, duration = 1.6, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const mv = useMotionValue(0);

  // parse the display string
  const match = String(value).match(/([^\d]*)([\d,]*\.?\d+)(.*)/);
  const prefix = match ? match[1] : "";
  const rawNum = match ? match[2].replace(/,/g, "") : "0";
  const suffix = match ? match[3] : "";
  const target = parseFloat(rawNum) || 0;
  const decimals = rawNum.includes(".") ? (rawNum.split(".")[1]?.length || 0) : 0;
  const useComma = String(value).includes(",");

  useEffect(() => {
    if (!inView) return;
    const controls = animate(mv, target, {
      duration,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (latest) => {
        if (!ref.current) return;
        let n = decimals
          ? latest.toFixed(decimals)
          : Math.round(latest).toString();
        if (useComma && !decimals) {
          n = Math.round(latest).toLocaleString("en-US");
        }
        ref.current.textContent = `${prefix}${n}${suffix}`;
      },
    });
    return () => controls.stop();
  }, [inView, target, duration, decimals, prefix, suffix, useComma, mv]);

  return (
    <span ref={ref} className={className}>
      {`${prefix}0${suffix}`}
    </span>
  );
}
